import { useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useNotify } from 'react-admin';
import type { Grid3DFormProps, Grid3DConfig, CriteriaWithLabel, Grid3DValidationResult } from './types';
import { Grid3DAxisConfig } from './Grid3DAxisConfig';
import { Grid3DPreview } from './Grid3DPreview';
import { apiPost, ApiClientError } from '../../utils/apiClient';

const createEmptyCriteria = (): CriteriaWithLabel => ({
  label: '',
  dsl: '',
  conditions: [],
});

const createDefaultConfig = (): Grid3DConfig => ({
  gameType: 'grid_3d',
  dimensions: { x: 3, y: 3, z: 3 },
  criteria: {
    x: [createEmptyCriteria(), createEmptyCriteria(), createEmptyCriteria()],
    y: [createEmptyCriteria(), createEmptyCriteria(), createEmptyCriteria()],
    z: [createEmptyCriteria(), createEmptyCriteria(), createEmptyCriteria()],
  },
});

export const Grid3DConfigForm = ({
  entityType,
  category,
  value,
  onChange,
  disabled = false,
}: Grid3DFormProps) => {
  const notify = useNotify();
  const [validationResult, setValidationResult] = useState<Grid3DValidationResult | undefined>();
  const [validating, setValidating] = useState(false);
  const [computing, setComputing] = useState(false);

  // Initialize with default config if empty
  const config = value?.gameType === 'grid_3d' ? value : createDefaultConfig();

  const handleAxisChange = useCallback(
    (axis: 'x' | 'y' | 'z', criteria: CriteriaWithLabel[]) => {
      const updated = {
        ...config,
        criteria: {
          ...config.criteria,
          [axis]: criteria,
        },
      };
      onChange(updated);
      // Clear validation when criteria change
      setValidationResult(undefined);
    },
    [config, onChange]
  );

  const handleValidate = useCallback(async () => {
    // Check if all criteria have at least one condition
    const allCriteria = [
      ...config.criteria.x,
      ...config.criteria.y,
      ...config.criteria.z,
    ];

    const emptyCriteria = allCriteria.filter((c) => c.conditions.length === 0);
    if (emptyCriteria.length > 0) {
      notify(`${emptyCriteria.length} criteria have no conditions. Please define conditions for all criteria.`, {
        type: 'warning',
      });
      return;
    }

    setValidating(true);

    try {
      const result = await apiPost<Grid3DValidationResult>(
        '/admin/api/game-configs/3d-grid/validate',
        {
          entityType,
          category,
          criteria: {
            x: config.criteria.x.map((c) => c.dsl),
            y: config.criteria.y.map((c) => c.dsl),
            z: config.criteria.z.map((c) => c.dsl),
          },
        }
      );

      setValidationResult(result);

      if (result.valid) {
        notify('All cells have valid answers!', { type: 'success' });
      } else {
        notify(`${result.errorCells} cells have no valid answers`, { type: 'warning' });
      }
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Validation failed';
      notify(message, { type: 'error' });
    } finally {
      setValidating(false);
    }
  }, [config, entityType, category, notify]);

  const handleComputeAnswers = useCallback(async () => {
    if (!validationResult?.valid) {
      notify('Please validate all cells first', { type: 'warning' });
      return;
    }

    setComputing(true);

    try {
      const result = await apiPost<{ answers: Record<string, string[]> }>(
        '/admin/api/game-configs/3d-grid/compute-answers',
        {
          entityType,
          category,
          criteria: {
            x: config.criteria.x.map((c) => c.dsl),
            y: config.criteria.y.map((c) => c.dsl),
            z: config.criteria.z.map((c) => c.dsl),
          },
        }
      );

      // Update config with computed answers
      const updated: Grid3DConfig = {
        ...config,
        answers: result.answers,
      };
      onChange(updated);

      notify('Answers computed and saved!', { type: 'success' });
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to compute answers';
      notify(message, { type: 'error' });
    } finally {
      setComputing(false);
    }
  }, [config, entityType, category, validationResult, onChange, notify]);

  const isConfigComplete = config.criteria.x.every((c) => c.conditions.length > 0) &&
    config.criteria.y.every((c) => c.conditions.length > 0) &&
    config.criteria.z.every((c) => c.conditions.length > 0);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        Configure a 3x3x3 grid game. Define 3 criteria for each axis (X=rows, Y=columns, Z=layers).
        Each cell requires entities that match all three criteria at once.
      </Alert>

      <Grid container spacing={3}>
        {/* Axis Configuration */}
        <Grid item xs={12} lg={6}>
          <Typography variant="h6" gutterBottom>
            Axis Configuration
          </Typography>

          <Grid3DAxisConfig
            axis="x"
            axisLabel="X (Row)"
            entityType={entityType}
            criteria={config.criteria.x}
            onChange={(criteria) => handleAxisChange('x', criteria)}
            disabled={disabled}
          />

          <Grid3DAxisConfig
            axis="y"
            axisLabel="Y (Column)"
            entityType={entityType}
            criteria={config.criteria.y}
            onChange={(criteria) => handleAxisChange('y', criteria)}
            disabled={disabled}
          />

          <Grid3DAxisConfig
            axis="z"
            axisLabel="Z (Layer)"
            entityType={entityType}
            criteria={config.criteria.z}
            onChange={(criteria) => handleAxisChange('z', criteria)}
            disabled={disabled}
          />
        </Grid>

        {/* Preview */}
        <Grid item xs={12} lg={6}>
          <Typography variant="h6" gutterBottom>
            Grid Preview & Validation
          </Typography>

          {!isConfigComplete ? (
            <Alert severity="warning">
              Please define conditions for all 9 criteria (3 per axis) before previewing.
            </Alert>
          ) : (
            <>
              <Grid3DPreview
                entityType={entityType}
                category={category}
                config={config}
                validationResult={validationResult}
                onValidate={handleValidate}
                loading={validating}
              />

              {validationResult?.valid && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={computing ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={handleComputeAnswers}
                    disabled={computing || disabled}
                    fullWidth
                  >
                    Compute & Save Answers
                  </Button>
                </Box>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
